import { useEffect, useState } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Plus, Pencil, Trash2, Eye, EyeOff, Upload, Ticket } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';

const empty = { title: '', description: '', date: '', end_date: '', location: '', address: '', image_url: '', ticket_url: '', capacity: '', price: '', tags: '', status: 'draft' };

export default function AdminEvents() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [uploading, setUploading] = useState(false);

  const load = () => api.get('/admin/events').then(r => setEvents(r.data)).catch(() => {}).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(empty); setEditId(null); setOpen(true); };
  const openEdit = (e) => {
    setForm({ ...e, tags: (e.tags || []).join(', '), capacity: e.capacity || '', price: e.price || '', ticket_url: e.ticket_url || '' });
    setEditId(e.event_id); setOpen(true);
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.post('/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setForm(p => ({ ...p, image_url: `${process.env.REACT_APP_BACKEND_URL}${data.url}` }));
      toast.success('Image uploadée');
    } catch { toast.error("Erreur upload"); }
    finally { setUploading(false); }
  };

  const handleSave = async () => {
    const payload = { ...form, tags: form.tags ? form.tags.split(',').map(t => t.trim()).filter(Boolean) : [],
      capacity: form.capacity ? parseInt(form.capacity) : null, price: form.price ? parseFloat(form.price) : 0 };
    try {
      if (editId) { await api.put(`/admin/events/${editId}`, payload); toast.success('Evenement mis a jour'); }
      else { await api.post('/admin/events', payload); toast.success('Evenement cree'); }
      setOpen(false); load();
    } catch { toast.error('Erreur'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cet evenement ?')) return;
    try { await api.delete(`/admin/events/${id}`); toast.success('Supprime'); load(); } catch { toast.error('Erreur'); }
  };

  const togglePublish = async (e) => {
    const newStatus = e.status === 'published' ? 'draft' : 'published';
    try { await api.put(`/admin/events/${e.event_id}`, { status: newStatus }); toast.success(newStatus === 'published' ? 'Publie' : 'Depublie'); load(); }
    catch { toast.error('Erreur'); }
  };

  const set = (k) => (e) => setForm(p => ({ ...p, [k]: typeof e === 'object' && e.target ? e.target.value : e }));

  if (loading) return <div className="flex justify-center py-20"><div className="spin-gold w-8 h-8" /></div>;

  return (
    <div className="fade-in" data-testid="admin-events-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-semibold tracking-tight" style={{ fontFamily: 'Outfit' }}>Evenements</h1>
        <Button onClick={openNew} data-testid="admin-create-event-btn" className="bg-[#D4AF37] text-black hover:bg-[#F9E5C0] rounded-full px-5">
          <Plus className="w-4 h-4 mr-2" /> Creer
        </Button>
      </div>

      <div className="space-y-3">
        {events.map(evt => (
          <div key={evt.event_id} className="bg-[#121212] border border-white/5 rounded-xl p-4 flex items-center gap-4" data-testid={`admin-event-${evt.event_id}`}>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className="font-semibold text-sm">{evt.title}</span>
                <Badge className={evt.status === 'published' ? 'bg-[#10B981]/15 text-[#10B981]' : 'bg-white/10 text-[#A1A1AA]'}>
                  {evt.status === 'published' ? 'Publie' : 'Brouillon'}
                </Badge>
              </div>
              <div className="text-xs text-[#A1A1AA]">
                {new Date(evt.date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })} - {evt.location}
                {evt.price > 0 && ` - ${evt.price.toFixed(2)} EUR`}
              </div>
            </div>
            <div className="flex gap-1 shrink-0">
              <Button size="icon" variant="ghost" onClick={() => togglePublish(evt)} data-testid={`toggle-publish-${evt.event_id}`}
                className="h-8 w-8 text-[#A1A1AA] hover:text-white">
                {evt.status === 'published' ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </Button>
              <Button size="icon" variant="ghost" onClick={() => openEdit(evt)} data-testid={`edit-event-${evt.event_id}`}
                className="h-8 w-8 text-[#A1A1AA] hover:text-white">
                <Pencil className="w-4 h-4" />
              </Button>
              <Button size="icon" variant="ghost" onClick={() => handleDelete(evt.event_id)} data-testid={`delete-event-${evt.event_id}`}
                className="h-8 w-8 text-red-400 hover:text-red-300">
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          </div>
        ))}
        {events.length === 0 && <div className="text-center py-16 text-[#A1A1AA]">Aucun evenement</div>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#0A0A0A] border-white/10 max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Outfit' }}>{editId ? 'Modifier' : 'Creer'} un evenement</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div><Label className="text-xs text-[#A1A1AA]">Titre *</Label>
              <Input value={form.title} onChange={set('title')} data-testid="event-form-title" className="bg-[#121212] border-white/10 rounded-xl mt-1" /></div>
            <div><Label className="text-xs text-[#A1A1AA]">Description</Label>
              <Textarea value={form.description} onChange={set('description')} rows={3} data-testid="event-form-desc" className="bg-[#121212] border-white/10 rounded-xl mt-1 resize-none" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs text-[#A1A1AA]">Date debut *</Label>
                <Input type="datetime-local" value={form.date} onChange={set('date')} data-testid="event-form-date" className="bg-[#121212] border-white/10 rounded-xl mt-1" /></div>
              <div><Label className="text-xs text-[#A1A1AA]">Date fin</Label>
                <Input type="datetime-local" value={form.end_date} onChange={set('end_date')} data-testid="event-form-enddate" className="bg-[#121212] border-white/10 rounded-xl mt-1" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs text-[#A1A1AA]">Lieu *</Label>
                <Input value={form.location} onChange={set('location')} data-testid="event-form-location" className="bg-[#121212] border-white/10 rounded-xl mt-1" /></div>
              <div><Label className="text-xs text-[#A1A1AA]">Adresse</Label>
                <Input value={form.address} onChange={set('address')} data-testid="event-form-address" className="bg-[#121212] border-white/10 rounded-xl mt-1" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs text-[#A1A1AA]">Capacite</Label>
                <Input type="number" value={form.capacity} onChange={set('capacity')} data-testid="event-form-capacity" className="bg-[#121212] border-white/10 rounded-xl mt-1" /></div>
              <div><Label className="text-xs text-[#A1A1AA]">Prix (EUR)</Label>
                <Input type="number" step="0.01" value={form.price} onChange={set('price')} data-testid="event-form-price" className="bg-[#121212] border-white/10 rounded-xl mt-1" /></div>
            </div>
            <div><Label className="text-xs text-[#A1A1AA]">Tags (virgule)</Label>
              <Input value={form.tags} onChange={set('tags')} placeholder="dj, mocktails" data-testid="event-form-tags" className="bg-[#121212] border-white/10 rounded-xl mt-1" /></div>
            <div><Label className="text-xs text-[#A1A1AA]">Lien billetterie (Weezevent, etc.)</Label>
              <div className="flex items-center gap-2 mt-1">
                <Ticket className="w-4 h-4 text-[#D4AF37] shrink-0" />
                <Input value={form.ticket_url} onChange={set('ticket_url')} placeholder="https://www.weezevent.com/..." data-testid="event-form-ticket" className="bg-[#121212] border-white/10 rounded-xl" />
              </div></div>
            <div><Label className="text-xs text-[#A1A1AA]">Image</Label>
              <div className="mt-1">
                {form.image_url && <img src={form.image_url} alt="" className="w-full h-32 object-cover rounded-xl mb-2" />}
                <label className="flex items-center gap-2 cursor-pointer bg-[#121212] border border-white/10 rounded-xl px-4 py-2.5 hover:border-[#D4AF37]/30 transition-colors">
                  <Upload className="w-4 h-4 text-[#D4AF37]" />
                  <span className="text-xs text-[#A1A1AA]">{uploading ? 'Upload en cours...' : 'Choisir une image'}</span>
                  <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" data-testid="event-form-image" />
                </label>
              </div></div>
            <div><Label className="text-xs text-[#A1A1AA]">Statut</Label>
              <Select value={form.status} onValueChange={set('status')}>
                <SelectTrigger className="bg-[#121212] border-white/10 rounded-xl mt-1" data-testid="event-form-status"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#121212] border-white/10">
                  <SelectItem value="draft">Brouillon</SelectItem>
                  <SelectItem value="published">Publie</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button onClick={handleSave} data-testid="event-form-save" className="w-full bg-[#D4AF37] text-black hover:bg-[#F9E5C0] rounded-full">
              {editId ? 'Mettre a jour' : 'Creer'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
